import type { ReactNode, JSX } from 'react'

import { Button, CoachMark, Flex } from '@buffer-mono/popcorn'

import { useJustJoinedCoachmark } from './useJustJoinedCoachmark'

// "Learn More" points at the "getting started as a user in an organization"
// help article. UTMs follow the app's standard "learn more" link convention
// (utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more).
const LEARN_MORE_URL =
  'https://support.buffer.com/articles/getting-started-as-a-user-in-an-organization-euygyfky4N?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more'

/**
 * The presentational just-joined coach mark: an open `CoachMark` spotlighting
 * the org-switcher trigger (`children`) with the team-account nudge copy. Split
 * out from the gated {@link OrgSwitcherCoachmark} so it can be rendered directly
 * in Storybook (and tweaked visually) without the localStorage/account gate.
 *
 * Always renders in the open state — visibility is the wrapper's job.
 */
export const OrgSwitcherCoachmarkContent = ({
  children,
  onDismiss,
}: {
  children: ReactNode
  onDismiss?: () => void
}): JSX.Element => {
  return (
    <CoachMark open onDismiss={onDismiss} dismissTimeout={null}>
      {/* Span the full trigger width so the ring centres over it. Set via
          `style` (not className) to preserve Popcorn's own `.overlay` class
          that positions the spotlight. */}
      <CoachMark.Overlay style={{ width: '100%' }}>
        {children}
        <CoachMark.Spotlight />
      </CoachMark.Overlay>
      <CoachMark.Content side="top" align="start" showCloseIcon={false}>
        <Flex direction="column" gap="md" align="stretch">
          <Flex direction="column" gap="xs">
            <CoachMark.Title>You&apos;re now in a team account</CoachMark.Title>
            <CoachMark.Description>
              This is separate from other Buffer teams. You can switch between
              them from the account menu.
            </CoachMark.Description>
          </Flex>
          <CoachMark.Footer>
            {/* Secondary action as a tertiary Button (matching the app's other
                two-action coach marks, e.g. SavedRepliesPopover /
                BulkActionsCoachmark) so it shares the primary button's height
                and baseline — a plain Link renders at the wrong size and sits
                misaligned. `as="a"` keeps it a real external anchor. */}
            <Button
              as="a"
              variant="tertiary"
              href={LEARN_MORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn More
            </Button>
            <CoachMark.Dismiss>
              <Button variant="primary">Got It</Button>
            </CoachMark.Dismiss>
          </CoachMark.Footer>
        </Flex>
      </CoachMark.Content>
    </CoachMark>
  )
}

/**
 * Coach mark that points a just-joined teammate at the organization switcher in
 * the bottom-left sidebar, so they know they're in a separate team account and
 * can switch between accounts (CORE-6022).
 *
 * Shown once, right after an existing user accepts an invitation and the app
 * reloads into the inviting org — gated by {@link useJustJoinedCoachmark}.
 * Brand-new users don't see it (they get the welcome modal instead).
 *
 * Wraps the org-switcher trigger (`children`) so the spotlight rings centre over
 * it; renders the trigger untouched when it shouldn't show.
 */
export const OrgSwitcherCoachmark = ({
  children,
}: {
  children: ReactNode
}): JSX.Element => {
  const { isOpen, dismiss } = useJustJoinedCoachmark()

  if (!isOpen) {
    return <>{children}</>
  }

  return (
    <OrgSwitcherCoachmarkContent onDismiss={dismiss}>
      {children}
    </OrgSwitcherCoachmarkContent>
  )
}
